import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import { useAddCatalogLanguage } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';

interface CatalogLanguageFormDialogProps {
  open: boolean;
  /** Whether the tenant already has at least one language (the very first is always the default). */
  hasExistingLanguages: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/** Add a catalog language — BCP-47/ISO 639-1 locale code + display name. */
export function CatalogLanguageFormDialog({
  open,
  hasExistingLanguages,
  onClose,
  onResult,
}: CatalogLanguageFormDialogProps) {
  const { t } = useTranslation('catalog');
  const addLanguage = useAddCatalogLanguage();

  const [locale, setLocale] = useState('');
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLocale('');
    setName('');
    setMakeDefault(false);
  }, [open]);

  const valid = locale.trim() !== '' && name.trim() !== '';

  const submit = () => {
    if (!valid) return;
    addLanguage.mutate(
      { locale: locale.trim(), name: name.trim(), makeDefault },
      {
        onSuccess: () => {
          onResult('success', t('catalogLanguageFormDialog.created', { name: name.trim() }));
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('catalogLanguageFormDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <DialogContentText>{t('catalogLanguageFormDialog.description')}</DialogContentText>
          <TextField
            label={t('catalogLanguageFormDialog.localeLabel')}
            required
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            error={locale.trim() === ''}
            helperText={t('catalogLanguageFormDialog.localeHelper')}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
          />
          <TextField
            label={t('catalogLanguageFormDialog.nameLabel')}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() === ''}
            fullWidth
          />
          {hasExistingLanguages ? (
            <FormControlLabel
              control={<Switch checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />}
              label={t('catalogLanguageFormDialog.makeDefaultLabel')}
            />
          ) : (
            <DialogContentText variant="caption">
              {t('catalogLanguageFormDialog.firstLanguageHint')}
            </DialogContentText>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={addLanguage.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || addLanguage.isPending}
          startIcon={addLanguage.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
